import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Calendar, ChevronDown, Sparkles } from 'lucide-react';

interface DatePickerProps {
  onDateSelect: (date: Date) => void;
  showYear?: boolean;
  buttonLabel?: string;
  theme?: 'light' | 'dark';
  /** Seeds the selection on mount, so returning to edit can retain the date. */
  initialDate?: Date | null;
}

const DatePicker: React.FC<DatePickerProps> = ({
  onDateSelect,
  showYear = false,
  buttonLabel = "Find My BirthVerse",
  theme = "light",
  initialDate = null
}) => {
  const validInitialDate = initialDate && Number.isFinite(initialDate.getTime()) ? initialDate : null;
  const [month, setMonth] = useState(() => validInitialDate ? (validInitialDate.getMonth() + 1).toString() : '');
  const [day, setDay] = useState(() => validInitialDate ? validInitialDate.getDate().toString() : '');
  const [year, setYear] = useState(() => showYear && validInitialDate ? validInitialDate.getFullYear().toString() : '');
  const [isMonthOpen, setIsMonthOpen] = useState(false);
  const [isDayOpen, setIsDayOpen] = useState(false);
  const [isYearOpen, setIsYearOpen] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const monthRef = useRef<HTMLButtonElement>(null);
  const dayRef = useRef<HTMLButtonElement>(null);
  const yearRef = useRef<HTMLButtonElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);
  const pendingFocus = useRef<'month' | 'day' | 'year' | 'submit' | null>(null);
  const id = useId();
  const monthId = `${id}-month`;
  const dayId = `${id}-day`;
  const yearId = `${id}-year`;

  const closePanels = useCallback(() => {
    setIsMonthOpen(false);
    setIsDayOpen(false);
    setIsYearOpen(false);
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event: PointerEvent) => {
      if (event.target instanceof Node && !formRef.current?.contains(event.target)) {
        closePanels();
      }
    };
    document.addEventListener('pointerdown', handleOutsideClick);
    return () => document.removeEventListener('pointerdown', handleOutsideClick);
  }, [closePanels]);

  // Options unmount on selection; focus after the next control has been enabled.
  useLayoutEffect(() => {
    const controls = { month: monthRef, day: dayRef, year: yearRef, submit: submitRef };
    if (pendingFocus.current) {
      controls[pendingFocus.current].current?.focus();
      pendingFocus.current = null;
    }
  });

  const handleEscape = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key !== 'Escape' || (!isMonthOpen && !isDayOpen && !isYearOpen)) return;
    event.preventDefault();
    const control = isMonthOpen ? monthRef : isDayOpen ? dayRef : yearRef;
    closePanels();
    control.current?.focus();
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => (currentYear - i).toString());

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (month && day && (!showYear || year)) {
      // If year is not shown, default to a leap year (2024) to handle Feb 29
      const selectedYear = showYear && year ? parseInt(year) : 2024;
      const date = new Date(selectedYear, parseInt(month) - 1, parseInt(day));
      if (date.getMonth() === parseInt(month) - 1 && date.getDate() === parseInt(day)) {
        onDateSelect(date);
      }
    }
  };

  const getDaysInMonth = (month: string, year: string) => {
    if (!month) return 31;
    // If year is not selected yet, assume leap year to show 29 days for Feb
    const y = showYear && year ? parseInt(year) : 2024;
    const daysInMonth = new Date(y, parseInt(month), 0).getDate();
    return daysInMonth;
  };

  const handleMonthSelect = (index: number) => {
    setMonth((index + 1).toString());
    setIsMonthOpen(false);
    setDay(''); // Reset day when month changes
    setIsDayOpen(true); // Open day selector after month selection
    pendingFocus.current = 'day';
  };

  const handleDaySelect = (day: number) => {
    setDay(day.toString());
    setIsDayOpen(false);
    if (showYear && !year) {
      setIsYearOpen(true);
      pendingFocus.current = 'year';
    } else {
      closePanels();
      pendingFocus.current = 'submit';
    }
  };

  const handleYearSelect = (year: string) => {
    setYear(year);
    setIsYearOpen(false);
    pendingFocus.current = 'year';
    if (parseInt(day) > getDaysInMonth(month, year)) {
      setDay('');
      setIsDayOpen(true);
      pendingFocus.current = 'day';
    }
  };

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      onKeyDown={handleEscape}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) closePanels();
      }}
      className="relative"
    >
      <div className="flex items-center justify-center space-x-3 mb-6 sm:mb-8">
        <div className="bg-[#F5F3FF] p-2 sm:p-2.5 rounded-xl">
          <Calendar aria-hidden="true" className="h-5 w-5 sm:h-6 sm:w-6 text-[#4F46E5]" />
        </div>
        <h2 className={`text-xl sm:text-2xl font-bold ${theme === "dark" ? "text-white" : "text-gray-900"}`}>Select Your Birthday</h2>
      </div>

      <div className={`grid grid-cols-1 ${showYear ? 'sm:grid-cols-3' : 'sm:grid-cols-2'} gap-4 sm:gap-6 mb-6 sm:mb-8`}>
        {/* Month Selector */}
        <div className="relative">
          <label htmlFor={monthId} className={`block text-sm sm:text-base font-semibold ${theme === "dark" ? "text-gray-200" : "text-gray-700"} mb-2`}>
            Month
          </label>
          <button
            ref={monthRef}
            id={monthId}
            aria-label={month ? months[parseInt(month) - 1] : 'Select month'}
            aria-expanded={isMonthOpen}
            aria-controls={isMonthOpen ? `${monthId}-options` : undefined}
            type="button"
            className={`w-full px-3 sm:px-4 py-3 sm:py-3.5 text-left bg-white border-2 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] transition-all duration-200 ${isMonthOpen ? 'border-[#4F46E5]' : 'border-gray-200 hover:border-[#4F46E5]'
              }`}
            onClick={() => {
              setIsMonthOpen(!isMonthOpen);
              setIsDayOpen(false);
              setIsYearOpen(false);
            }}
          >
            <div className="flex items-center justify-between">
              <span className={`text-sm sm:text-base ${month ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                {month ? months[parseInt(month) - 1] : 'Select month'}
              </span>
              <ChevronDown aria-hidden="true" className={`h-4 w-4 sm:h-5 sm:w-5 text-gray-400 transition-transform ${isMonthOpen ? 'transform rotate-180' : ''
                }`} />
            </div>
          </button>

          {isMonthOpen && (
            <div id={`${monthId}-options`} className="absolute z-20 w-full mt-2 bg-white border-2 border-[#4F46E5] rounded-xl shadow-lg max-h-48 sm:max-h-60 overflow-auto">
              <div className="py-1">
                {months.map((monthName, index) => (
                  <button
                    key={monthName}
                    type="button"
                    className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-left text-sm sm:text-base hover:bg-[#F5F3FF] transition-colors ${month === (index + 1).toString() ? 'bg-[#F5F3FF] text-[#4F46E5] font-semibold' : 'text-gray-700'
                      }`}
                    onClick={() => handleMonthSelect(index)}
                  >
                    {monthName}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Day Selector */}
        <div className="relative">
          <label htmlFor={dayId} className={`block text-sm sm:text-base font-semibold ${theme === "dark" ? "text-gray-200" : "text-gray-700"} mb-2`}>
            Day
          </label>
          <button
            ref={dayRef}
            id={dayId}
            aria-label={day ? `Day ${day}` : 'Select day'}
            aria-expanded={isDayOpen}
            aria-controls={isDayOpen ? `${dayId}-options` : undefined}
            type="button"
            className={`w-full px-3 sm:px-4 py-3 sm:py-3.5 text-left bg-white border-2 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] transition-all duration-200 ${isDayOpen ? 'border-[#4F46E5]' : 'border-gray-200 hover:border-[#4F46E5]'
              } ${!month ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={() => {
              if (month) {
                setIsDayOpen(!isDayOpen);
                setIsMonthOpen(false);
                setIsYearOpen(false);
              }
            }}
            disabled={!month}
          >
            <div className="flex items-center justify-between">
              <span className={`text-sm sm:text-base ${day ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                {day ? `Day ${day}` : 'Select day'}
              </span>
              <ChevronDown aria-hidden="true" className={`h-4 w-4 sm:h-5 sm:w-5 text-gray-400 transition-transform ${isDayOpen ? 'transform rotate-180' : ''
                }`} />
            </div>
          </button>

          {isDayOpen && (
            <div id={`${dayId}-options`} className="absolute z-20 w-full mt-2 bg-white border-2 border-[#4F46E5] rounded-xl shadow-lg max-h-48 sm:max-h-60 overflow-auto">
              <div className="py-2 grid grid-cols-5 sm:grid-cols-7 gap-1 p-2">
                {[...Array(getDaysInMonth(month, year))].map((_, i) => (
                  <button
                    key={i + 1}
                    type="button"
                    className={`aspect-square rounded-lg flex items-center justify-center text-sm sm:text-base hover:bg-[#F5F3FF] transition-colors ${day === (i + 1).toString()
                      ? 'bg-[#F5F3FF] text-[#4F46E5] font-semibold'
                      : 'text-gray-700'
                      }`}
                    onClick={() => handleDaySelect(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Year Selector */}
        {showYear && (
          <div className="relative">
            <label htmlFor={yearId} className={`block text-sm sm:text-base font-semibold ${theme === "dark" ? "text-gray-200" : "text-gray-700"} mb-2`}>
              Year
            </label>
            <button
              ref={yearRef}
              id={yearId}
              aria-label={year || 'Select year'}
              aria-expanded={isYearOpen}
              aria-controls={isYearOpen ? `${yearId}-options` : undefined}
              type="button"
              className={`w-full px-3 sm:px-4 py-3 sm:py-3.5 text-left bg-white border-2 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] transition-all duration-200 ${isYearOpen ? 'border-[#4F46E5]' : 'border-gray-200 hover:border-[#4F46E5]'
                } ${!day ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => {
                if (day) {
                  setIsYearOpen(!isYearOpen);
                  setIsMonthOpen(false);
                  setIsDayOpen(false);
                }
              }}
              disabled={!day}
            >
              <div className="flex items-center justify-between">
                <span className={`text-sm sm:text-base ${year ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                  {year ? year : 'Select year'}
                </span>
                <ChevronDown aria-hidden="true" className={`h-4 w-4 sm:h-5 sm:w-5 text-gray-400 transition-transform ${isYearOpen ? 'transform rotate-180' : ''
                  }`} />
              </div>
            </button>

            {isYearOpen && (
              <div id={`${yearId}-options`} className="absolute z-20 w-full mt-2 bg-white border-2 border-[#4F46E5] rounded-xl shadow-lg max-h-48 sm:max-h-60 overflow-auto">
                <div className="py-1">
                  {years.map((y) => (
                    <button
                      key={y}
                      type="button"
                      className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-left text-sm sm:text-base hover:bg-[#F5F3FF] transition-colors ${year === y ? 'bg-[#F5F3FF] text-[#4F46E5] font-semibold' : 'text-gray-700'
                        }`}
                      onClick={() => handleYearSelect(y)}
                    >
                      {y}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <button
        ref={submitRef}
        type="submit"
        disabled={!month || !day || (showYear && !year)}
        className={`relative w-full py-3 sm:py-4 rounded-xl font-bold text-base sm:text-lg transition-all duration-300 ${month && day && (!showYear || year)
          ? 'bg-gradient-to-r from-[#4F46E5] to-[#7C3AED] text-white hover:scale-[1.02] hover:shadow-lg'
          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
      >
        <div className="flex items-center justify-center space-x-2">
          <span>{buttonLabel}</span>
          {month && day && (!showYear || year) && <Sparkles aria-hidden="true" className="h-4 w-4 sm:h-5 sm:w-5" />}
        </div>
        {month && day && (!showYear || year) && (
          <div className="absolute inset-0 bg-white rounded-xl opacity-0 hover:opacity-10 transition-opacity" />
        )}
      </button>
    </form>
  );
};

export default DatePicker;
